import { create } from 'zustand';
import type { LocalUserSnapshot } from '../../../entities/user/model/user.types';
import type { PublicUser } from '../../../shared/api/types';
import { ApiError, NetworkError, isNetworkError } from '../../../shared/api/client';
import {
  fetchMe,
  loginRequest,
  logoutRequest,
  registerRequest,
  updateTimezoneRequest,
} from '../api/auth.api';
import {
  META_KEYS,
  clearLocalUser,
  readLocalUserId,
  readMeta,
  writeLocalUser,
} from '../../sync/db/meta.repo';
import { clearAllLocalUserData } from '../../sync/db/entities.repo';
import { useSyncStore } from '../../sync/model/sync.store';

export type AuthStatus = 'booting' | 'authenticated' | 'anonymous';
export type AuthActionStatus = 'idle' | 'pending';

interface AuthState {
  status: AuthStatus;
  user: LocalUserSnapshot | null;
  actionStatus: AuthActionStatus;
  bootError: string | null;
  bootstrap: () => Promise<void>;
  login: (login: string, password: string) => Promise<void>;
  register: (login: string, password: string, inviteCode: string) => Promise<void>;
  logout: (options?: { server?: boolean }) => Promise<void>;
  handleUnauthorized: () => Promise<void>;
  /** Смена часовой пояса через PATCH /api/auth/me + локальный snapshot. */
  setTimezone: (timezone: string) => Promise<void>;
}

function toLocalUser(user: PublicUser): LocalUserSnapshot {
  return {
    id: user.id,
    login: user.login,
    role: user.role,
    isAdmin: user.isAdmin || user.role === 'admin',
    timezone: user.timezone,
  };
}

async function loadLocalSnapshot(): Promise<LocalUserSnapshot | null> {
  const id = await readMeta<string | null>(META_KEYS.userId, null);
  if (id === null || id.length === 0) {
    return null;
  }
  const login = await readMeta<string>(META_KEYS.userLogin, '');
  const role = await readMeta<string>(META_KEYS.userRole, 'user');
  const isAdmin = await readMeta<boolean>(META_KEYS.userIsAdmin, role === 'admin');
  const timezone = await readMeta<string>(META_KEYS.userTimezone, 'Asia/Yekaterinburg');
  return {
    id,
    login,
    role: role === 'admin' ? 'admin' : 'user',
    isAdmin,
    timezone,
  };
}

async function startSyncForUser(user: LocalUserSnapshot): Promise<void> {
  await useSyncStore.getState().start(user.id);
}

async function stopSync(): Promise<void> {
  useSyncStore.getState().stop();
}

export const useAuthStore = create<AuthState>((set, get) => ({
  status: 'booting',
  user: null,
  actionStatus: 'idle',
  bootError: null,

  bootstrap: async () => {
    set({ status: 'booting', bootError: null });

    const local = await loadLocalSnapshot();
    if (local === null) {
      set({ status: 'anonymous', user: null });
      return;
    }

    set({ status: 'authenticated', user: local });

    // Пробуем освежить сессию. Офлайн — остаёмся на локальном user_id.
    try {
      const me = await fetchMe();
      const snapshot = toLocalUser(me);
      await writeLocalUser({
        id: snapshot.id,
        login: snapshot.login,
        role: snapshot.role,
        isAdmin: snapshot.isAdmin,
        timezone: snapshot.timezone,
      });
      set({ status: 'authenticated', user: snapshot, bootError: null });
      await startSyncForUser(snapshot);
    } catch (error) {
      if (isNetworkError(error)) {
        await startSyncForUser(local);
        set({ bootError: null });
        return;
      }

      if (error instanceof ApiError && error.status === 401) {
        await stopSync();
        await clearLocalUser();
        await clearAllLocalUserData();
        set({ status: 'anonymous', user: null, bootError: null });
        return;
      }

      if (error instanceof NetworkError) {
        await startSyncForUser(local);
        return;
      }

      // Прочие ошибки сервера: остаёмся офлайн-сессией, но помним текст.
      await startSyncForUser(local);
      set({ bootError: error instanceof Error ? error.message : 'Не удалось проверить сессию' });
    }
  },

  login: async (login, password) => {
    set({ actionStatus: 'pending' });
    try {
      const user = await loginRequest(login, password);
      const snapshot = toLocalUser(user);

      const previous = await readLocalUserId();
      if (previous !== null && previous !== snapshot.id) {
        await clearAllLocalUserData();
      }

      await writeLocalUser({
        id: snapshot.id,
        login: snapshot.login,
        role: snapshot.role,
        isAdmin: snapshot.isAdmin,
        timezone: snapshot.timezone,
      });
      set({ status: 'authenticated', user: snapshot, actionStatus: 'idle', bootError: null });
      await startSyncForUser(snapshot);
    } catch (error) {
      set({ actionStatus: 'idle' });
      throw error;
    }
  },

  register: async (login, password, inviteCode) => {
    set({ actionStatus: 'pending' });
    try {
      const user = await registerRequest(login, password, inviteCode);
      const snapshot = toLocalUser(user);
      await writeLocalUser({
        id: snapshot.id,
        login: snapshot.login,
        role: snapshot.role,
        isAdmin: snapshot.isAdmin,
        timezone: snapshot.timezone,
      });
      set({ status: 'authenticated', user: snapshot, actionStatus: 'idle', bootError: null });
      await startSyncForUser(snapshot);
    } catch (error) {
      set({ actionStatus: 'idle' });
      throw error;
    }
  },

  logout: async (options) => {
    const server = options?.server ?? true;
    await stopSync();
    if (server) {
      try {
        await logoutRequest();
      } catch {
        // офлайн или сессия уже мертва — локально выходим всё равно
      }
    }
    await clearLocalUser();
    await clearAllLocalUserData();
    set({ status: 'anonymous', user: null, actionStatus: 'idle', bootError: null });
  },

  handleUnauthorized: async () => {
    const current = get().status;
    if (current === 'anonymous') {
      return;
    }
    await stopSync();
    await clearLocalUser();
    // Не чистим entities при 401, если это временный серверный отказ? По ТЗ: logout + redirect.
    // Данные пользователя на устройстве остаются до явного logout/другого аккаунта —
    // но 401 означает, что cookie невалидна. Оставляем outbox, чтобы не потерять правки.
    set({ status: 'anonymous', user: null, actionStatus: 'idle' });
  },

  setTimezone: async (timezone: string) => {
    const current = get().user;
    if (current === null) {
      throw new Error('Пользователь не авторизован');
    }
    const trimmed = timezone.trim();
    if (trimmed.length === 0) {
      throw new Error('Укажите часовой пояс');
    }
    const user = await updateTimezoneRequest(trimmed);
    const snapshot = toLocalUser(user);
    await writeLocalUser({
      id: snapshot.id,
      login: snapshot.login,
      role: snapshot.role,
      isAdmin: snapshot.isAdmin,
      timezone: snapshot.timezone,
    });
    set({ user: snapshot });
  },
}));
